import { useMutateUpdateChatTitle } from '@/hooks/mutation/useMutateUpdateChatTitle';
import type { ChatListItem } from '@/types';
import { useCallback, useState } from 'react';

interface EditingState {
  chatId: string;
  section: string;
}

export function useRenameChat() {
  const { mutate: updateChatTitle } = useMutateUpdateChatTitle();
  const [editingState, setEditingState] = useState<EditingState | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [originalTitle, setOriginalTitle] = useState('');

  const handleStartRename = useCallback((chat: ChatListItem, section: string) => {
    setEditingState({ chatId: chat.id, section });
    setEditingTitle(chat.title);
    setOriginalTitle(chat.title);
  }, []);

  const handleSubmitRename = useCallback(
    (chatId: string) => {
      const title = editingTitle.trim();
      if (!title || title === originalTitle.trim()) {
        setEditingState(null);
        setEditingTitle('');
        return;
      }
      updateChatTitle(
        { chatId, title },
        { onSettled: () => setEditingState(null) }
      );
    },
    [editingTitle, originalTitle, updateChatTitle]
  );

  const handleCancelRename = useCallback(() => {
    setEditingState(null);
    setEditingTitle('');
  }, []);

  const isEditingChat = useCallback(
    (chatId: string, section: string) =>
      editingState?.chatId === chatId && editingState?.section === section,
    [editingState]
  );

  return {
    editingState,
    editingTitle,
    setEditingTitle,
    handleStartRename,
    handleSubmitRename,
    handleCancelRename,
    isEditingChat,
  };
}
